// Side sheet / panel component that slides in from the left or right edge of the screen.
// Used for detail views, forms, and contextual content — similar to Azure portal side panels.
// Kendo React has no native side-sheet; this is a composite using a portal, CSS transitions,
// and Kendo layout primitives.
import * as React from "react";
import { createPortal } from "react-dom";
import classNames from "classnames";
import { StackLayout } from "@progress/kendo-react-layout";
import { CPText, Container, IconButton } from "shared/components/base";
import "./CPActionsheetModalStyles.scss";

export interface CPActionsheetModalProps {
  /** Header title displayed at the top of the panel */
  title: string;
  /** Controls visibility; triggers slide-in / slide-out animation */
  open: boolean;
  /** Called when the user clicks the backdrop or the close button, or presses Escape */
  onClose: () => void;
  /** Which edge the panel slides in from (default: "right") */
  position?: "left" | "right";
  /** Explicit width — accepts a pixel number or any valid CSS width string, e.g. "50%" */
  width?: number | string;
  /** Minimum width in pixels (default: 400) */
  minWidth?: number;
  /** Main body content */
  children?: React.ReactNode;
  /** Optional footer action buttons rendered at the bottom of the panel */
  actionBtns?: React.ReactNode;
  /** Render action buttons stacked vertically instead of horizontally (default: false) */
  actionBtnsDisplayVertical?: boolean;
  /** Inner padding of the content area — number (px) or CSS string (default: 15) */
  contentPadding?: number | string;
  /** Additional CSS class applied to the panel element */
  className?: string;
  /** Inline styles merged onto the panel element */
  style?: React.CSSProperties;
}

/**
 * Side-sheet / action-sheet panel that slides in from the left or right edge of the screen.
 * Rendered via a React portal so it sits above all other content. Supports an optional footer
 * row for action buttons and closes on backdrop click or `Escape` key press.
 *
 * @example
 * ```tsx
 * <CPActionsheetModal
 *   title="Edit Shipment"
 *   open={isPanelOpen}
 *   onClose={() => setIsPanelOpen(false)}
 *   width={600}
 *   actionBtns={
 *     <>
 *       <CPButton fillMode="outline" onClick={() => setIsPanelOpen(false)}>Cancel</CPButton>
 *       <CPButton themeColor="primary" showLoader={isSaving} onClick={handleSave}>Save</CPButton>
 *     </>
 *   }
 * >
 *   <ShipmentForm shipment={selectedShipment} onChange={handleChange} />
 * </CPActionsheetModal>
 * ```
 */
const CPActionsheetModal: React.FC<CPActionsheetModalProps> = ({
  title,
  open,
  onClose,
  position = "right",
  width,
  minWidth = 400,
  children,
  actionBtns,
  actionBtnsDisplayVertical = false,
  contentPadding = 0,
  className,
  style,
}) => {
  // `mounted` keeps the portal in the DOM during the close animation.
  // `isOpen` applies the CSS class that triggers the slide-in transition.
  const [mounted, setMounted] = React.useState(open);
  const [isOpen, setIsOpen] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setMounted(true);
      // Double RAF ensures the element is in the DOM before the transition class is added.
      const frameId = requestAnimationFrame(() => {
        requestAnimationFrame(() => setIsOpen(true));
      });
      return () => cancelAnimationFrame(frameId);
    } else {
      setIsOpen(false);
      // Keep mounted until the CSS transition (300 ms) completes, then unmount.
      const timer = setTimeout(() => setMounted(false), 300);
      return () => clearTimeout(timer);
    }
  }, [open]);

  // Close on Escape key while the panel is visible.
  React.useEffect(() => {
    if (!mounted) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [mounted, onClose]);

  if (!mounted) return null;

  const panelStyle: React.CSSProperties = {
    ...style,
    minWidth,
    ...(width != null ? { width } : {}),
  };

  return createPortal(
    <>
      {/* Semi-transparent backdrop — clicking it closes the panel */}
      <div
        className={classNames("cp-actionsheet-overlay", {
          "cp-actionsheet-overlay-open": isOpen,
        })}
        onClick={onClose}
      />

      {/* Side panel */}
      <div
        className={classNames(
          "cp-actionsheet-panel",
          `cp-actionsheet-${position}`,
          { "cp-actionsheet-panel-open": isOpen },
          className,
        )}
        style={panelStyle}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        {/* Header */}
        <div className="cp-actionsheet-header">
          <CPText textStyle="subtitle2">{title}</CPText>
          <IconButton icon="x" onClick={onClose} />
        </div>

        {/* Scrollable content area */}
        <div className="cp-actionsheet-content">
          <Container padding={contentPadding}>{children}</Container>
        </div>

        {/* Optional footer with action buttons */}
        {actionBtns != null && (
          <div className="cp-actionsheet-footer">
            <StackLayout
              gap={10}
              orientation={
                actionBtnsDisplayVertical ? "vertical" : "horizontal"
              }
            >
              {actionBtns}
            </StackLayout>
          </div>
        )}
      </div>
    </>,
    document.body,
  );
};

export default CPActionsheetModal;
